import { getMediaProvider, getMediaSearchChannels } from '../../providers/registry.js'
import {
    mediaReferenceKey,
    normalizeMediaReference,
    mediaSnapshotForReference,
    mediaIdentityKey,
} from '../../utils/media.js'

let searchSequence = 0
let activeSearchController = null
let detailSequence = 0
let activeDetailController = null
let loadingSequence = 0
const detailsCache = new Map()
const DETAILS_CACHE_TTL = 30 * 60 * 1000

function emptySection(channel) {
    return {
        id: channel.id,
        title: channel.title,
        results: [],
        status: 'idle',
        error: '',
    }
}

function initialSections() {
    return Object.fromEntries(getMediaSearchChannels().map((channel) => [channel.id, emptySection(channel)]))
}

function isAbortError(error) {
    return error && (
        error.name === 'AbortError'
        || error.name === 'CanceledError'
        || error.code === 'ERR_CANCELED'
    )
}

function routeScope() {
    if (typeof window === 'undefined') return 'media'
    return `${window.location.pathname}${window.location.search}`
}

async function withLoading(ctx, operation) {
    let token = `media-${++loadingSequence}`
    ctx.commit('BEGIN_LOADING_REQUEST', { token, scope: routeScope() }, { root: true })
    try {
        return await operation()
    } finally {
        ctx.commit('END_LOADING_REQUEST', token, { root: true })
    }
}

async function searchChannel(channel, query, signal, options = {}) {
    let channelProviders = channel.providerIds
        .map(getMediaProvider)
        .filter(Boolean)
    if (!channelProviders.length) throw new Error(`No provider configured for ${channel.id}`)

    let settled = await Promise.allSettled(
        channelProviders.map((provider) => provider.search(query, {
            signal,
            compact: options.compact === true,
        }))
    )
    let successful = settled.filter((result) => result.status === 'fulfilled')
    if (!successful.length) throw settled[0].reason

    let seen = new Set()
    return successful
        .flatMap((result) => result.value || [])
        .filter((item) => {
            if (!item) return false
            let ids = item.ids || {}
            let identities = [
                ids.kinopoisk && `${item.kind}:kinopoisk:${ids.kinopoisk}`,
                ids.imdb && `${item.kind}:imdb:${ids.imdb}`,
                ids.tmdb && `${item.kind}:tmdb:${ids.tmdb}`,
                ids.shikimori && `${item.kind}:shikimori:${ids.shikimori}`,
            ].filter(Boolean)
            if (!identities.length) identities = [`${item.kind}:${item.provider}:${item.id}`]
            if (identities.some((identity) => seen.has(identity))) return false
            identities.forEach((identity) => seen.add(identity))
            return true
        })
}

function providersForReference(reference) {
    let channelId = reference.kind === 'anime' ? 'anime' : 'cinema'
    let channel = getMediaSearchChannels().find((item) => item.id === channelId)
    let ids = (channel ? channel.providerIds : [reference.provider]).filter(Boolean)
    return [...new Set(ids)].map(getMediaProvider).filter(Boolean)
}

async function resolveWithProvider(provider, reference, signal) {
    if (typeof provider.resolve === 'function') {
        return provider.resolve(reference, { signal })
    }
    if (provider.id === reference.provider && reference.providerId && typeof provider.getDetails === 'function') {
        return provider.getDetails(reference.providerId, { signal })
    }
    if (
        reference.source === 'provider'
        && reference.provider === provider.id
        && typeof provider.getDetails === 'function'
    ) {
        return provider.getDetails(reference.id, { signal })
    }
    return null
}

function readDetailsCache(key) {
    let cached = detailsCache.get(key)
    if (!cached || Date.now() - cached.savedAt > DETAILS_CACHE_TTL) {
        detailsCache.delete(key)
        return null
    }
    return cached.item
}

export default {
    state: {
        media_search_query: '',
        media_search_sections: initialSections(),
        media_details: {
            key: '',
            item: null,
            status: 'idle',
            error: '',
        },
    },
    getters: {
        get_media_search_query: (state) => state.media_search_query,
        get_media_search_sections: (state) => getMediaSearchChannels()
            .map((channel) => state.media_search_sections[channel.id] || emptySection(channel)),
        get_media_details: (state) => state.media_details.item,
        get_media_details_key: (state) => state.media_details.key,
        get_media_details_status: (state) => state.media_details.status,
        get_media_details_error: (state) => state.media_details.error,
    },
    actions: {
        async search_media(ctx, query) {
            let requestSequence = ++searchSequence
            if (activeSearchController) activeSearchController.abort()
            let normalizedQuery = String(query || '').trim()
            ctx.commit('SET_MEDIA_SEARCH_QUERY', normalizedQuery)

            if (!normalizedQuery) {
                activeSearchController = null
                ctx.commit('RESET_MEDIA_SEARCH')
                return
            }

            let controller = typeof AbortController === 'undefined' ? null : new AbortController()
            activeSearchController = controller
            let channels = getMediaSearchChannels()
            channels.forEach((channel) => ctx.commit('SET_MEDIA_SEARCH_SECTION', {
                id: channel.id,
                data: { results: [], status: 'loading', error: '' },
            }))

            let tasks = channels.map(async (channel) => {
                try {
                    let results = await searchChannel(
                        channel,
                        normalizedQuery,
                        controller ? controller.signal : undefined,
                        { compact: true }
                    )
                    if (requestSequence !== searchSequence) return
                    ctx.commit('SET_MEDIA_SEARCH_SECTION', {
                        id: channel.id,
                        data: { results, status: 'success', error: '' },
                    })
                } catch (error) {
                    if (requestSequence !== searchSequence || isAbortError(error)) return
                    ctx.commit('SET_MEDIA_SEARCH_SECTION', {
                        id: channel.id,
                        data: {
                            results: [],
                            status: 'error',
                            error: `Не удалось загрузить раздел «${channel.title}».`,
                        },
                    })
                }
            })

            await Promise.allSettled(tasks)
            if (activeSearchController === controller) activeSearchController = null
        },
        invalidate_media_search(ctx) {
            searchSequence++
            if (activeSearchController) activeSearchController.abort()
            activeSearchController = null
            ctx.commit('SET_MEDIA_SEARCH_QUERY', '')
            ctx.commit('RESET_MEDIA_SEARCH')
        },
        async request_media_details(ctx, payload) {
            let reference = normalizeMediaReference(payload)
            const records = Object.values(ctx.rootGetters?.get_library_state?.items || {})
            const snapshot = mediaSnapshotForReference(reference, [
                ...records.map((record) => record.media),
                reference.snapshot,
                ...(ctx.rootGetters?.get_worth_watching || []),
                ...Object.values(ctx.state.media_search_sections).flatMap((section) => section.results || []),
            ])
            if (snapshot) reference = { ...reference, snapshot }
            let key = mediaReferenceKey(reference)
            if (ctx.state.media_details.key === key && ctx.state.media_details.status === 'success') {
                return ctx.state.media_details.item
            }

            let requestSequence = ++detailSequence
            if (activeDetailController) activeDetailController.abort()
            activeDetailController = null

            if (!reference.id) {
                ctx.commit('SET_MEDIA_DETAILS', {
                    key,
                    item: null,
                    status: 'error',
                    error: 'В ссылке нет идентификатора материала.',
                })
                return null
            }

            let cached = readDetailsCache(key)
            if (cached) {
                ctx.commit('SET_MEDIA_DETAILS', { key, item: cached, status: 'success', error: '' })
                return cached
            }

            let providers = providersForReference(reference)
            if (!providers.length) {
                ctx.commit('SET_MEDIA_DETAILS', {
                    key,
                    item: null,
                    status: 'error',
                    error: 'Нет подключённого видеодвижка для этого раздела.',
                })
                return null
            }

            let controller = typeof AbortController === 'undefined' ? null : new AbortController()
            activeDetailController = controller
            ctx.commit('SET_MEDIA_DETAILS', { key, item: null, status: 'loading', error: '' })

            return withLoading(ctx, async () => {
                let lastError = null
                try {
                    for (const provider of providers) {
                        try {
                            let item = await resolveWithProvider(
                                provider,
                                reference,
                                controller ? controller.signal : undefined
                            )
                            if (requestSequence !== detailSequence) return null
                            if (!item || item.kind !== reference.kind) continue
                            detailsCache.set(key, { savedAt: Date.now(), item })
                            ctx.commit('SET_MEDIA_DETAILS', { key, item, status: 'success', error: '' })
                            return item
                        } catch (error) {
                            if (isAbortError(error)) throw error
                            lastError = error
                        }
                    }
                    ctx.commit('SET_MEDIA_DETAILS', {
                        key,
                        item: null,
                        status: lastError ? 'error' : 'not-found',
                        error: lastError?.code === 'MEDIA_ID_UNAVAILABLE'
                            ? 'В старой записи нет IMDb или TMDB ID. Найдите фильм заново через поиск.'
                            : lastError
                            ? 'Не удалось загрузить информацию. Попробуйте ещё раз.'
                            : 'Фильм, сериал или аниме не найдены.',
                    })
                    return null
                } catch (error) {
                    if (requestSequence !== detailSequence || isAbortError(error)) return null
                    ctx.commit('SET_MEDIA_DETAILS', {
                        key,
                        item: null,
                        status: 'error',
                        error: 'Не удалось загрузить информацию. Попробуйте ещё раз.',
                    })
                    return null
                } finally {
                    if (activeDetailController === controller) activeDetailController = null
                }
            })
        },
        async resolve_media_player(ctx, payload) {
            let reference = normalizeMediaReference(payload)
            let key = mediaReferenceKey(reference)
            let cachedMedia = ctx.state.media_details.key === key
                ? ctx.state.media_details.item
                : readDetailsCache(key)
            let media = cachedMedia || await ctx.dispatch('request_media_details', reference)
            if (!media) return null
            let resolvedProvider = getMediaProvider(media.provider)
            const record = ctx.rootGetters?.get_library_item?.(mediaIdentityKey(media))
            return resolvedProvider && typeof resolvedProvider.getPlayer === 'function'
                ? resolvedProvider.getPlayer(media, { progress: record?.progress || {} })
                : null
        },
    },
    mutations: {
        SET_MEDIA_SEARCH_QUERY(state, query) {
            state.media_search_query = query
        },
        RESET_MEDIA_SEARCH(state) {
            state.media_search_sections = initialSections()
        },
        SET_MEDIA_SEARCH_SECTION(state, { id, data }) {
            let current = state.media_search_sections[id] || {}
            state.media_search_sections[id] = { ...current, ...data }
        },
        SET_MEDIA_DETAILS(state, details) {
            state.media_details = details
        },
    },
}
